import React, { useState, useEffect, useContext } from "react";

import {
  Scale,
  ArrowRight,
  Sparkles,
  PlayCircle,
  ShieldCheck,
  BarChart3,
  XCircle,
  CheckCircle,
  Download,
  FileText,
  Users,
  Bot,
  Database,
  Settings,
  Search,
  Mail,
  CalendarDays,
  FolderOpen,
  MessageCircle,
  ReceiptText,
  Mic,
  LockKeyhole,
  Gavel,
} from "lucide-react";

import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";

import Head from "../../Components/Layout/Head";
import FooterNew from "../../Components/Layout/FooterNew";
import { CountryContext } from "../../CountryContext";
import ApiPath from "../../Components/Common/Apiurl";

import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet";
import getHelmetContent from "../../Components/Common/HelmetData";

const countryOptions = [
  "Australia",
  "Germany",
  "India",
  "New Zealand",
  "United States",
  "Other",
];

const legalFeatures = [
  {
    id: 1,
    icon: <FileText size={20} />,
    name: "Smart Document & Draft Assistance",
    purpose: "Legal drafting / document preparation",
    currentChallenge:
      "Lawyers and staff manually prepare notices, agreements, petitions, replies and routine drafts from scattered case inputs.",
    aiAction:
      "AI creates first-level legal drafts from basic matter inputs, selected draft type and preferred language.",
    benefit:
      "Faster first drafts, consistent document structure, fewer formatting mistakes and better focus on legal strategy.",
    implementation:
      "Can support English, Marathi or Hindi templates for notices, agreements, petitions, sale deeds, replies and other reusable legal drafts.",
  },
  {
    id: 2,
    icon: <CalendarDays size={20} />,
    name: "Court Date & Hearing Tracker",
    purpose: "Case calendar / hearing management",
    currentChallenge:
      "Court dates, deadlines and follow-ups are often tracked manually through diaries, Excel sheets or staff reminders.",
    aiAction:
      "AI-enabled tracker organizes hearing dates, deadlines, reminders and case-wise calendar events.",
    benefit:
      "Lower risk of missed dates, better case calendar discipline and improved team preparedness.",
    implementation:
      "Can integrate with case records, Google Calendar, internal reminders, WhatsApp/SMS alerts and role-wise notifications.",
  },
  {
    id: 3,
    icon: <Mail size={20} />,
    name: "Email to Case Data Auto Extraction",
    purpose: "Client email / matter intake",
    currentChallenge:
      "Client emails and attachments are manually read, copied and converted into case data or task records.",
    aiAction:
      "AI reads emails and attachments, extracts key client and matter details, and maps them to structured case records.",
    benefit:
      "Less manual data entry, better organized client information and faster matter creation.",
    implementation:
      "Can extract client name, contact details, case reference, invoice information, requested action, document type and attached files.",
  },
  {
    id: 4,
    icon: <Users size={20} />,
    name: "Task & Team Management",
    purpose: "Internal legal operations",
    currentChallenge:
      "Task ownership, due dates and follow-ups are managed manually, causing coordination gaps between seniors, juniors and admin staff.",
    aiAction:
      "AI-assisted workflow assigns tasks, tracks ownership, monitors progress and highlights pending work.",
    benefit:
      "Better accountability, clearer work allocation and reduced internal follow-up effort.",
    implementation:
      "Can include task assignment, status tracking, priority flags, deadline alerts, user roles and dashboard visibility.",
  },
  {
    id: 5,
    icon: <FolderOpen size={20} />,
    name: "Document Checklist Automation",
    purpose: "Case preparation / document collection",
    currentChallenge:
      "Required document lists are manually prepared for each matter type, increasing the chance of missing important documents.",
    aiAction:
      "AI generates matter-specific document checklists based on case type, draft type or legal workflow.",
    benefit:
      "Faster case preparation, fewer missing documents and better client guidance.",
    implementation:
      "Can support property matters, civil cases, criminal cases, family matters, notices, agreements and compliance-related checklists.",
  },
  {
    id: 6,
    icon: <MessageCircle size={20} />,
    name: "AI Email Reply Suggestions",
    purpose: "Client communication",
    currentChallenge:
      "Client replies require time to draft professionally, especially when matter context must be considered.",
    aiAction:
      "AI suggests professional replies based on email content, case context and preferred tone.",
    benefit:
      "Faster client responses, consistent communication style and improved client experience.",
    implementation:
      "Can generate replies for document requests, hearing updates, payment follow-ups, appointment scheduling and case status communication.",
  },
  {
    id: 7,
    icon: <Database size={20} />,
    name: "Google Drive Integration",
    purpose: "Document access / case repository",
    currentChallenge:
      "Case documents are stored in folders but are difficult to search, link and retrieve quickly during legal work.",
    aiAction:
      "AI connects existing Drive folders with case records and enables structured document search.",
    benefit:
      "Centralized access without forcing the firm to change existing storage habits.",
    implementation:
      "Can map Drive folders to clients, matters, document categories, invoices, drafts, notices and uploaded case files.",
  },
  {
    id: 8,
    icon: <MessageCircle size={20} />,
    name: "WhatsApp / SMS Notifications",
    purpose: "Client updates / reminders",
    currentChallenge:
      "Staff manually call or message clients for hearing reminders, document requests and case updates.",
    aiAction:
      "System sends automated or review-based notifications through WhatsApp/SMS.",
    benefit:
      "Improved client communication, fewer manual calls and better follow-up discipline.",
    implementation:
      "Can support hearing reminders, document pending alerts, invoice reminders, appointment confirmations and status updates.",
  },
  {
    id: 9,
    icon: <ReceiptText size={20} />,
    name: "Invoice & Billing Automation",
    purpose: "Accounts / billing",
    currentChallenge:
      "Invoice creation, payment tracking and billing follow-ups are often handled manually.",
    aiAction:
      "AI-assisted billing creates invoice drafts, tracks payment status and links billing with case records.",
    benefit:
      "Cleaner revenue visibility, fewer missed payments and more organized billing operations.",
    implementation:
      "Can generate editable invoice forms, PDF invoices, payment status dashboards and case-wise billing records.",
  },
  {
    id: 10,
    icon: <Mic size={20} />,
    name: "Audio Chat & Speech-to-Text Voice Input",
    purpose: "Dictation / drafting / notes",
    currentChallenge:
      "Lawyers often need to type long notes, draft instructions, case facts or client communication manually.",
    aiAction:
      "AI converts voice input into text and can convert dictated points into notes, drafts or email content.",
    benefit:
      "Quick capture of legal thoughts, faster note preparation and easier drafting support.",
    implementation:
      "Can support voice-based case notes, draft instructions, hearing notes, client meeting summaries and multilingual input where required.",
  },
  {
    id: 11,
    icon: <LockKeyhole size={20} />,
    name: "User Roles & Permissions",
    purpose: "Confidentiality / access control",
    currentChallenge:
      "Manual access control creates risk of accidental misuse or unnecessary exposure of confidential case data.",
    aiAction:
      "Role-based access controls who can view, edit, approve, download or manage specific data.",
    benefit:
      "Better confidentiality, safer collaboration and controlled access for admin, senior, junior and accounts users.",
    implementation:
      "Can include senior lawyer, junior lawyer, admin, accounts and read-only user roles with audit trails.",
  },
  {
    id: 12,
    icon: <Gavel size={20} />,
    name: "Judgment Summary Tool",
    purpose: "Judgment review / legal research",
    currentChallenge:
      "Long judgments and court orders take time to read, understand and convert into useful legal points.",
    aiAction:
      "AI summarizes court orders and judgments into issues, facts, findings, ratio, important observations and action points.",
    benefit:
      "Quicker understanding of rulings, faster research and better preparation for drafting and arguments.",
    implementation:
      "Can support PDF upload, key point extraction, case-law summary, legal issue tagging and matter-wise reference storage.",
  },
  {
    id: 13,
    icon: <BarChart3 size={20} />,
    name: "Legal Analytics Dashboard",
    purpose: "Management visibility",
    currentChallenge:
      "Partners and management may not have a consolidated view of active cases, pending work, billing and team performance.",
    aiAction:
      "Dashboard shows case status, pending tasks, hearing calendar, billing status and team workload.",
    benefit:
      "Better management decisions, stronger performance visibility and improved operational control.",
    implementation:
      "Can include matter status, client-wise work, pending hearings, team task load, invoice status and monthly activity dashboards.",
  },
  {
    id: 14,
    icon: <Search size={20} />,
    name: "Case Research",
    purpose: "Legal research / precedent search",
    currentChallenge:
      "Manual case-law research takes time and requires reviewing multiple judgments, legal references and previous matter notes.",
    aiAction:
      "AI searches relevant case laws, prior judgments, legal references and matter-specific precedents based on case facts or keywords.",
    benefit:
      "Faster legal research, stronger case preparation and better support for drafting arguments and opinions.",
    implementation:
      "Can support keyword search, fact-based research, cited answers, judgment summaries and internal knowledge-base search.",
  },
];

const featureCategories = [
  "All",
  "Drafting",
  "Case Management",
  "Client Communication",
  "Billing",
  "Security",
  "Research",
];

const getFeatureCategory = (purpose, name) => {
  const text = `${purpose} ${name}`.toLowerCase();

  
  if (
    text.includes("invoice") ||
    text.includes("billing") ||
    text.includes("accounts")
  ) {
    return "Billing";
  }
  
  if (
    text.includes("draft") ||
    text.includes("document preparation") ||
    text.includes("checklist") ||
    text.includes("dictation") ||
    text.includes("voice")
  ) {
    return "Drafting";
  }

  if (
    text.includes("calendar") ||
    text.includes("hearing") ||
    text.includes("task") ||
    text.includes("drive") ||
    text.includes("repository") ||
    text.includes("dashboard") ||
    text.includes("management visibility")
  ) {
    return "Case Management";
  }

  if (
    text.includes("email") ||
    text.includes("whatsapp") ||
    text.includes("sms") ||
    text.includes("client communication") ||
    text.includes("client updates")
  ) {
    return "Client Communication";
  }


  if (
    text.includes("permission") ||
    text.includes("confidential") ||
    text.includes("access control")
  ) {
    return "Security";
  }

  if (
    text.includes("judgment") ||
    text.includes("research") ||
    text.includes("case research")
  ) {
    return "Research";
  }

  return "Case Management";
};

const valueAreas = [
  {
    icon: <FileText size={24} />,
    title: "Legal Drafting",
    text: "Draft notices, agreements, petitions, replies, case notes and client communication with review-ready AI assistance.",
  },
  {
    icon: <FolderOpen size={24} />,
    title: "Case Organization",
    text: "Connect emails, documents, tasks, hearings, invoices and client information into structured matter records.",
  },
  {
    icon: <MessageCircle size={24} />,
    title: "Client Communication",
    text: "Generate reply suggestions, reminders, WhatsApp/SMS updates and follow-up messages for better client handling.",
  },
  {
    icon: <ShieldCheck size={24} />,
    title: "Confidential AI",
    text: "Enable role-based access, controlled document handling and secure deployment options for sensitive legal data.",
  },
];

const implementationAreas = [
  {
    icon: <Database size={22} />,
    title: "Connect Existing Data",
    text: "The AI layer can connect with existing folders, Google Drive, case documents, emails and billing files without replacing current workflows.",
  },
  {
    icon: <Bot size={22} />,
    title: "Create Legal AI Assistants",
    text: "Role-specific assistants can support drafting, research, email replies, document checklists, billing and client updates.",
  },
  {
    icon: <ShieldCheck size={22} />,
    title: "Protect Confidential Data",
    text: "User roles, permissions, audit trails and private deployment options help maintain confidentiality and controlled access.",
  },
  {
    icon: <Settings size={22} />,
    title: "Implement In Phases",
    text: "Start with document search, drafting and reminders, then expand to billing, analytics, research and advanced automation.",
  },
];

const consultationPoints = [
  "AI legal draft generation",
  "Case document search",
  "Client communication automation",
  "Secure role-based access",
];

const LawAndLegalAI = () => {
  const [selectedCountry, setSelectedCountry] = useState("India");
  const [activeCategory, setActiveCategory] = useState("All");

  const country = useContext(CountryContext);
  const websiteURL = window.location.origin;

  const [formVisible, setFormVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [phone, setPhone] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    country: "",
    message: "",
    firmType: "Law and Legal",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    firmType: "",
    message: "",
  });

  useEffect(() => {
    AOS.init({
      duration: 900,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });
  }, []);

  useEffect(() => {
    if (countryOptions.includes(country)) {
      setSelectedCountry(country);
    } else {
      setSelectedCountry("India");
    }
  }, [country]);

  const filteredFeatures =
    activeCategory === "All"
      ? legalFeatures
      : legalFeatures.filter(
          (item) => getFeatureCategory(item.purpose, item.name) === activeCategory
        );

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    setErrors({
      ...errors,
      [name]: "",
    });

    if (name === "country") {
      setSelectedCountry(value);
      setPhone("");

      setErrors((prev) => ({
        ...prev,
        phone: "",
        country: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {
      name: "",
      email: "",
      phone: "",
      country: "",
      firmType: "",
      message: "",
    };

    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    const emailRegex = /^\S+@\S+\.\S+$/;

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!phone) {
      newErrors.phone = "Phone number is required";
      isValid = false;
    } else if (!isValidPhoneNumber(phone)) {
      newErrors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    if (!selectedCountry) {
      newErrors.country = "Please select country";
      isValid = false;
    }

    if (formData.message.trim().length > 300) {
      newErrors.message = "Maximum character limit is 300";
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) return;

    const payload = {
      name: formData.name,
      email: formData.email.trim(),
      phone: phone,
      lookingFor: "Law and Legal",
      queryPageIndex: "Law_and_Legal_PAGE",
      message:
        (formData.message || "") +
        ` IP: ${country} dropdown: ${selectedCountry} | URL: ${websiteURL}`,
    };

    try {
      setLoading(true);
      setFormError("");

      const response = await fetch(
        ApiPath + "/api/enquiryController/tqAiEnquiry",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        setFormData({
          name: "",
          email: "",
          firmType: "Law and Legal",
          message: "",
        });

        if (window.fbq) {
          window.fbq("track", "Lead");
        }

        setPhone("");
        setFormVisible(false);

        setTimeout(() => {
          setFormVisible(true);
          setLoading(false);
        }, 5000);
      } else {
        setFormError("Something went wrong. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      setFormError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const getCountryCode = (countryName) => {
    const map = {
      India: "IN",
      "United States": "US",
      Australia: "AU",
      Germany: "DE",
      Japan: "JP",
      Singapore: "SG",
      "New Zealand": "NZ",
    };

    return map[countryName] || "IN";
  };

  const pageTitle =
    "AI Automation For Law and Legal Operations - ThinkQuotient AI";
  const metaTitle =
    "AI Automation For Law and Legal Operations - ThinkQuotient AI";
  const helmetContent = getHelmetContent();

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={metaTitle} />

        {helmetContent.meta.map((metaTag, index) => (
          <meta key={index} name={metaTag.name} content={metaTag.content} />
        ))}

        {helmetContent.script}

        <script>
          {`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-KTNBVH9V');
          `}
        </script>

        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=UA-143016865-1"
        ></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || []; 
            function gtag() { 
              dataLayer.push(arguments); 
            } 
            gtag("js", new Date()); 
            gtag("config", "UA-143016865-1");
          `}
        </script>

        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-BSFPPN5E9D"
        ></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag() { 
              dataLayer.push(arguments); 
            }
            gtag('js', new Date());
            gtag('config', 'G-BSFPPN5E9D');
          `}
        </script>

        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=AW-574047969"
        ></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag() { 
              dataLayer.push(arguments); 
            }
            gtag('js', new Date());
            gtag('config', 'AW-574047969');
          `}
        </script>

        <noscript>{`<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-KTNBVH9V" height="0" width="0" style="display:none;visibility:hidden"></iframe>`}</noscript>

        <script>{`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '1317328810324214');
          fbq('track', 'PageView');
        `}</script>

        <noscript>{`<img height="1" width="1" style="display:none"
          src="https://www.facebook.com/tr?id=1317328810324214&ev=PageView&noscript=1"
        />`}</noscript>
      </Helmet>

      <Head />

      <div className="relative min-h-screen overflow-hidden bg-slate-50 text-slate-900">
        {/* HERO */}
        <section
          className="relative overflow-hidden bg-cover bg-center bg-no-repeat py-16 sm:py-20 lg:py-12"
          style={{
            backgroundImage:
              "url('/assets/images/futuristic_legal_digital_abstract_design.png')",
          }}
        >
          <div className="absolute inset-0 bg-slate-950/35"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/82 via-blue-950/45 to-slate-950/45"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/25 via-transparent to-slate-950/60"></div>

          <div
            data-aos="zoom-in"
            data-aos-delay="100"
            className="absolute -left-24 top-10 h-96 w-96 rounded-full bg-blue-400/10 blur-3xl"
          ></div>

          <div
            data-aos="zoom-in"
            data-aos-delay="250"
            className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-indigo-500/20 blur-3xl"
          ></div>

          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 items-center gap-12 xl:gap-20 lg:grid-cols-12">
              <div
                className="text-center lg:col-span-7 xl:col-span-7 lg:text-left"
                data-aos="fade-right"
              >
                <div
                  data-aos="fade-up"
                  data-aos-delay="100"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-5 py-3 text-sm font-bold text-white backdrop-blur-md"
                >
                  <Sparkles size={16} className="text-cyan-300" />
                  AI Solutions For Law Firms
                </div>

                <h1
                  data-aos="fade-up"
                  data-aos-delay="200"
                  className="mt-8 text-4xl font-black leading-[1.02] tracking-[-0.04em] text-white sm:text-5xl xl:text-5xl"
                >
                  AI Automation
                  <span className="mt-3 block bg-gradient-to-r from-cyan-300 via-blue-200 to-white bg-clip-text py-2 text-transparent">
                    For Law & Legal Firms
                  </span>
                </h1>

                <p
                  data-aos="fade-up"
                  data-aos-delay="300"
                  className="mx-auto mt-5 max-w-2xl text-base leading-8 text-white/80 sm:text-lg lg:mx-0"
                >
                  Automate legal drafting, case research, court date tracking,
                  client communication, billing, document search and secure legal
                  operations with AI-powered workflows designed for law firms.
                </p>

                <div
                  data-aos="fade-up"
                  data-aos-delay="400"
                  className="mt-10 flex flex-col justify-center gap-4 sm:flex-row sm:flex-wrap lg:justify-start"
                >
                  <a
                    href="#contact-form"
                    className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-8 py-4 font-bold text-slate-950 shadow-xl transition-all duration-300 hover:-translate-y-1 hover:bg-cyan-50"
                  >
                    Get AI Consultation
                    <ArrowRight
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </a>

                  <a
                    href="#demo-video"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-8 py-4 font-bold text-white shadow-lg backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:bg-white/15"
                  >
                    Watch Demo
                    <PlayCircle size={18} />
                  </a>

                  <a
                    href="/assets/pdfs/AI Feature Benefit Report for Legal Practice.pdf"
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-8 py-4 font-bold text-white shadow-lg backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:bg-white/15"
                  >
                    Report
                    <Download size={18} />
                  </a>
                </div>
              </div>

              <div
                className="relative lg:col-span-5 xl:col-span-5"
                data-aos="fade-left"
                data-aos-delay="250"
              >
                <div className="absolute -inset-4 rounded-[2.8rem] bg-cyan-400/15 blur-2xl"></div>

                <div className="relative overflow-hidden rounded-[2.5rem] border border-white/20 bg-white/10 p-4 shadow-[0_30px_90px_rgba(0,0,0,0.35)] backdrop-blur-xl">
                  <img
                    src="/assets/images/lady-justice-statue-front-courthouse.jpg"
                    alt="AI for Law Firms"
                    className="h-[360px] w-full rounded-[2rem] object-cover sm:h-[430px]"
                  />

                  <div className="absolute inset-4 rounded-[2rem] bg-gradient-to-t from-slate-950/75 via-transparent to-transparent"></div>

                  <div className="absolute inset-x-8 bottom-8 rounded-3xl border border-white/20 bg-slate-950/45 p-5 text-white backdrop-blur-xl">
                    <div className="inline-flex items-center gap-2 rounded-full bg-cyan-400/15 px-3 py-1 text-xs font-bold text-cyan-200">
                      <Scale size={13} />
                      Secure Legal AI
                    </div>

                    <p className="mt-3 text-base font-bold sm:text-lg">
                      AI layer for legal operations, not a replacement for lawyers.
                    </p>

                    <p className="mt-2 text-sm leading-6 text-white/75">
                      Supports drafting, research, reminders, billing and
                      document search with controlled access.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AI FEATURES SECTION */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl"></div>
          <div className="absolute -right-24 bottom-20 h-72 w-72 rounded-full bg-indigo-100/70 blur-3xl"></div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-start gap-10 lg:grid-cols-12">
              <div className="lg:col-span-8" data-aos="fade-right">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-5 py-3 text-sm font-bold text-blue-700 shadow-sm">
                  <BarChart3 size={16} />
                  AI Feature Benefits
                </div>

                <h2 className="mt-6 text-4xl font-black leading-tight tracking-[-0.04em] text-slate-900 lg:text-5xl">
                  Practical AI Modules For Law Firms
                </h2>

                <p className="mt-6 max-w-3xl text-lg leading-9 text-slate-600">
                  Explore AI modules that support drafting, research, case
                  management, client communication, billing, document search and
                  confidential legal operations.
                </p>
              </div>

              <div className="lg:col-span-4" data-aos="fade-left">
                <div className="rounded-[2rem] border border-blue-100 bg-white p-6 shadow-[0_20px_70px_rgba(15,23,42,0.08)]">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                    <ShieldCheck size={26} />
                  </div>

                  <h3 className="mt-5 text-xl font-black text-slate-900">
                    Confidential AI Layer
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    The AI system works on top of documents, emails, folders,
                    calendars, billing records and internal workflows with
                    role-based access control.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-12 flex flex-wrap gap-3" data-aos="fade-up">
              {featureCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    activeCategory === category
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                      : "border border-slate-200 bg-white text-slate-600 hover:border-blue-100 hover:bg-blue-50 hover:text-blue-700"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {filteredFeatures.map((item, index) => (
                <div
                  key={item.id}
                  data-aos="fade-up"
                  data-aos-delay={(index % 6) * 80}
                  className="group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-[0_24px_80px_rgba(15,23,42,0.10)]"
                >
                  <div className="absolute left-0 top-0 h-1.5 w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>

                  <div className="flex items-start justify-between gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                      {item.icon}
                    </div>

                    <span className="rounded-full bg-slate-50 px-3 py-1 text-xs font-bold text-slate-500">
                      {getFeatureCategory(item.purpose, item.name)}
                    </span>
                  </div>

                  <h3 className="mt-5 text-lg font-black leading-7 text-slate-900">
                    {item.name}
                  </h3>

                  <p className="mt-3 text-sm font-semibold text-blue-700">
                    {item.purpose}
                  </p>

                  <div className="mt-5 space-y-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                        Current Challenge
                      </p>
                      <p className="mt-2 text-sm leading-7 text-slate-600">
                        {item.currentChallenge}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                        What AI Does
                      </p>
                      <p className="mt-2 text-sm leading-7 text-slate-600">
                        {item.aiAction}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 rounded-2xl bg-blue-50 p-4">
                    <div className="flex gap-3">
                      <CheckCircle
                        size={18}
                        className="mt-0.5 shrink-0 text-blue-600"
                      />
                      <p className="text-sm font-semibold leading-7 text-blue-800">
                        {item.benefit}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-6 text-xs leading-6 text-slate-500">
              *The above modules are for solution planning. Final implementation
              scope depends on firm workflow, document formats, folder structure,
              user roles, security policy and integration requirements.
            </p>
          </div>
        </section>

        {/* VALUE AREAS */}
        <section className="bg-white py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center" data-aos="fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-5 py-3 text-sm font-bold text-blue-700">
                <Sparkles size={16} />
                AI Opportunity Areas
              </div>

              <h2 className="mt-6 text-4xl font-black leading-tight tracking-[-0.04em] text-slate-900 lg:text-5xl">
                Where AI Creates Value In Legal Operations
              </h2>

              <p className="mt-6 text-lg leading-9 text-slate-600">
                Start with practical legal workflows and then expand into
                advanced drafting, research, analytics and client communication
                automation.
              </p>
            </div>

            <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
              {valueAreas.map((item, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(15,23,42,0.10)]"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>

                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                    {item.icon}
                  </div>

                  <h3 className="mt-6 text-xl font-black text-slate-900">
                    {item.title}
                  </h3>

                  <p className="mt-4 text-sm leading-7 text-slate-600">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* IMPLEMENTATION APPROACH */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl"></div>
          <div className="absolute -right-24 bottom-20 h-72 w-72 rounded-full bg-indigo-100/70 blur-3xl"></div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center" data-aos="fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-5 py-3 text-sm font-bold text-blue-700 shadow-sm">
                <Settings size={16} />
                Implementation Approach
              </div>

              <h2 className="mt-6 text-4xl font-black leading-tight tracking-[-0.04em] text-slate-900 lg:text-5xl">
                AI Layer On Top Of Existing Legal Workflows
              </h2>

              <p className="mt-6 text-lg leading-9 text-slate-600">
                The solution can be implemented in phases without disturbing
                existing folders, email habits, billing processes, calendar
                tracking or drafting workflows.
              </p>
            </div>

            <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
              {implementationAreas.map((item, index) => (
                <div
                  key={index}
                  className="group rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(15,23,42,0.10)]"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50 text-slate-700 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                    {item.icon}
                  </div>

                  <h3 className="mt-6 text-xl font-black text-slate-900">
                    {item.title}
                  </h3>

                  <p className="mt-4 text-sm leading-7 text-slate-600">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>

            <div
              data-aos="fade-up"
              data-aos-delay="300"
              className="mt-12 rounded-[2rem] border border-blue-100 bg-blue-50/70 p-6"
            >
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-blue-600">
                    <LockKeyhole size={22} />
                  </div>

                  <div>
                    <h3 className="font-black text-slate-900">
                      Confidential Access
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-slate-600">
                      Role-wise access for senior lawyers, junior lawyers,
                      admin, accounts and read-only users.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-blue-600">
                    <Bot size={22} />
                  </div>

                  <div>
                    <h3 className="font-black text-slate-900">
                      Assistive Legal AI
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-slate-600">
                      AI supports drafting, research and summaries while final
                      review remains with lawyers.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-blue-600">
                    <FolderOpen size={22} />
                  </div>

                  <div>
                    <h3 className="font-black text-slate-900">
                      Modular Rollout
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-slate-600">
                      Start with document search and drafting, then expand into
                      reminders, billing, analytics and research.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* VIDEO */}
        <section id="demo-video" className="bg-white py-20">
          <div className="mx-auto max-w-7xl px-0 sm:px-6 lg:px-8 md:px-4">
            <div className="px-4 text-center" data-aos="fade-up">
              <div className="inline-flex items-center gap-2 border border-blue-100 bg-blue-50 px-5 py-3 text-sm font-bold text-blue-700 md:rounded-full">
                <PlayCircle size={16} />
                AI Legal Workflow Demo
              </div>

              <h2 className="mt-6 text-4xl font-black leading-tight tracking-[-0.04em] text-slate-900 lg:text-5xl">
                See How AI Can Transform Legal Operations
              </h2>

              <p className="mx-auto mt-6 max-w-3xl text-lg leading-9 text-slate-600">
                Practical automation workflows for modern legal practices and
                law firms.
              </p>
            </div>

            <div
              className="mt-12 max-w-5xl justify-self-center border border-slate-200 bg-white p-5 shadow-[0_20px_70px_rgba(15,23,42,0.06)] md:rounded-[2.5rem] lg:p-7"
              data-aos="zoom-in-up"
              data-aos-delay="150"
            >
              <div className="overflow-hidden bg-black md:rounded-[2rem]">
                <video
                  className="aspect-video w-full object-contain"
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  <source src="/assets/videos/LawAndLegal.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT FORM SECTION */}
        <section
          id="contact-form"
          className="relative overflow-hidden py-20 sm:py-24"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50"></div>

          <div className="relative mx-auto max-w-7xl px-0 sm:px-6 lg:px-8 md:px-4">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 xl:grid-cols-5">
              <div
                className="px-6 lg:col-span-1 xl:col-span-3"
                data-aos="fade-right"
              >
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-100 px-4 py-2 text-sm font-semibold text-blue-700">
                  <Sparkles size={16} />
                  AI Consultation For Law Firms
                </div>

                <h2 className="mt-6 text-4xl font-black leading-tight text-slate-900 sm:text-5xl">
                  Let’s Build
                  <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    AI For Your Legal Practice
                  </span>
                </h2>

                <p className="mt-6 max-w-xl text-lg leading-8 text-slate-600">
                  Automate legal drafting, case document search, client
                  communication, hearing reminders, billing workflows and secure
                  legal knowledge management.
                </p>

                <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {consultationPoints.map((item, index) => (
                    <div
                      key={index}
                      data-aos="fade-up"
                      data-aos-delay={index * 100}
                      className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                        <CheckCircle size={18} />
                      </div>

                      <span className="font-medium text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className="relative lg:col-span-1 xl:col-span-2"
                data-aos="fade-left"
                data-aos-delay="100"
              >
                <div className="absolute -inset-2 rounded-[2rem] bg-gradient-to-r from-blue-500 to-indigo-600 opacity-20 blur-xl"></div>

                <div className="relative overflow-hidden border border-white/40 bg-white/90 shadow-[0_25px_80px_rgba(0,0,0,0.08)] backdrop-blur-xl md:rounded-[2rem]">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-8 text-white">
                    <div className="flex items-center gap-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-md">
                        <Scale size={28} />
                      </div>

                      <div>
                        <h3 className="text-2xl font-bold">
                          Book AI Consultation
                        </h3>
                        <p className="mt-1 text-sm text-blue-100">
                          Tell us about your legal automation requirements
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 sm:p-8">
                    {!formVisible && (
                      <div className="mb-6 rounded-2xl border border-blue-200 bg-blue-50 px-5 py-6 text-blue-700">
                        <div className="flex flex-col items-center text-center">
                          <CheckCircle
                            size={52}
                            className="mb-4 text-blue-600"
                          />
                          <h3 className="mb-2 text-xl font-bold">
                            Thank You!
                          </h3>
                          <p className="text-sm sm:text-base">
                            Our team will contact you shortly.
                          </p>
                        </div>
                      </div>
                    )}

                    {formError && (
                      <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-6 text-red-700">
                        <div className="flex flex-col items-center text-center">
                          <XCircle
                            size={48}
                            className="mb-3 text-red-500"
                          />
                          <h3 className="mb-2 text-lg font-bold">
                            Submission Failed
                          </h3>
                          <p className="text-sm">{formError}</p>
                        </div>
                      </div>
                    )}

                    {formVisible && (
                      <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                          <input
                            type="text"
                            name="name"
                            placeholder="Full Name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${
                              errors.name
                                ? "border-red-500"
                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                            }`}
                          />
                          {errors.name && (
                            <p className="mt-2 text-sm text-red-500">
                              {errors.name}
                            </p>
                          )}
                        </div>

                        <div>
                          <div
                            className={`rounded-2xl border bg-slate-50 px-4 py-4 transition-all duration-300 ${
                              errors.phone
                                ? "border-red-500"
                                : "border-slate-200 focus-within:border-blue-500 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-100"
                            }`}
                          >
                            <PhoneInput
                              key={selectedCountry}
                              countries={[
                                "IN",
                                "US",
                                "AU",
                                "DE",
                                "JP",
                                "SG",
                                "NZ",
                              ]}
                              defaultCountry={getCountryCode(selectedCountry)}
                              international
                              value={phone}
                              onChange={(value) => {
                                setPhone(value || "");
                                setErrors({ ...errors, phone: "" });
                              }}
                              placeholder="Enter phone number"
                            />
                          </div>
                          {errors.phone && (
                            <p className="mt-2 text-sm text-red-500">
                              {errors.phone}
                            </p>
                          )}
                        </div>

                        <div>
                          <input
                            type="email"
                            name="email"
                            placeholder="Email Address"
                            value={formData.email}
                            onChange={handleChange}
                            className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${
                              errors.email
                                ? "border-red-500"
                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                            }`}
                          />
                          {errors.email && (
                            <p className="mt-2 text-sm text-red-500">
                              {errors.email}
                            </p>
                          )}
                        </div>

                        <div>
                          <select
                            id="country"
                            name="country"
                            className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${
                              errors.country
                                ? "border-red-500"
                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                            }`}
                            onChange={handleChange}
                            value={selectedCountry}
                          >
                            {countryOptions.map((countryName) => (
                              <option key={countryName} value={countryName}>
                                {countryName}
                              </option>
                            ))}
                          </select>
                          {errors.country && (
                            <p className="mt-2 text-sm text-red-500">
                              {errors.country}
                            </p>
                          )}
                        </div>

                        <div>
                          <textarea
                            rows={3}
                            name="message"
                            placeholder="Tell us which legal workflows, drafting activities, case documents, client communication, billing process or research work you want to automate"
                            value={formData.message}
                            onChange={handleChange}
                            className={`w-full resize-none rounded-2xl bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${
                              errors.message
                                ? "border border-red-500"
                                : "border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                            }`}
                          />
                          {errors.message && (
                            <p className="mt-2 text-sm text-red-500">
                              {errors.message}
                            </p>
                          )}
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-lg font-semibold text-white transition-all duration-300 ${
                            loading
                              ? "cursor-not-allowed bg-slate-400"
                              : "bg-gradient-to-r from-blue-600 to-indigo-600 shadow-xl hover:scale-[1.02]"
                          }`}
                        >
                          {loading
                            ? "Submitting..."
                            : "Request AI Consultation"}
                          {!loading && <ArrowRight size={18} />}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <FooterNew />
      </div>
    </>
  );
};

export default LawAndLegalAI;